import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { combineLatest, Observable, throwError, timer } from 'rxjs';
import { filter, map, mergeMap, retryWhen, switchMap, tap } from 'rxjs/operators';
import { OfflineStrategy, OfflineStrategyInjectionToken } from '../../interfaces/offline-strategy.interface';
import { OfflineService } from '../../service/offline.service';
import { deleteCache, hydrate, hydrateFailure, hydrateSuccess } from '../actions/hydration.actions';
import { selectOfflineFeature } from '../selectors/pace-offline-feature.selector';
import { PaceOfflineFeatureState } from '../state';

// https://www.learnrxjs.io/learn-rxjs/operators/error_handling/retrywhen
export const genericRetryStrategy =
    (maxRetryAttempts: number) =>
    (attempts: Observable<unknown>): Observable<number> => {
        return attempts.pipe(
            mergeMap((error, i) => {
                const retryAttempt = i + 1;
                // if maximum number of retries have been met
                if (retryAttempt > maxRetryAttempts) {
                    return throwError(() => error);
                }
                const millisecondsForASecond = 1000;
                // retry after 1s, 2s, etc...
                return timer(retryAttempt * millisecondsForASecond);
            }),
        );
    };

const retryAttempts = 5;

@Injectable()
export class HydrationEffects implements OnInitEffects {
    private readonly maxRetryAttempts = retryAttempts;
    public constructor(
        private action$: Actions,
        private store: Store<PaceOfflineFeatureState>,
        @Inject(OfflineStrategyInjectionToken) private offlineStrategy: OfflineStrategy,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private offlineService: OfflineService,
    ) {}

    public ngrxOnInitEffects(): Action {
        return hydrate();
    }

    public hydrate$ = createEffect(() =>
        this.action$.pipe(
            ofType(hydrate),
            mergeMap(() => {
                this.log.debug(`HydrationEffects: hydrate$: getting state`);
                return this.offlineStrategy.getState();
            }),
            map((state) => {
                if (!state) {
                    this.log.debug(`HydrationEffects: hydrate$: getState failure`);
                    return hydrateFailure();
                }
                this.log.debug(`HydrationEffects: hydrate$: getState success`);
                return hydrateSuccess({ state });
            }),
        ),
    );

    public serialize$ = createEffect(
        () =>
            this.action$.pipe(
                ofType(hydrateSuccess, hydrateFailure),
                switchMap(() =>
                    combineLatest([
                        this.offlineService.isAppInitialized$.pipe(
                            tap((isAppInitialized) =>
                                this.log.debug(`HydrationEffects: serialize$: isAppInitialized: ${isAppInitialized}`),
                            ),
                        ),
                        this.store
                            .select(selectOfflineFeature)
                            .pipe(
                                tap((store) =>
                                    this.log.debug(
                                        `HydrationEffects: serialize$: selectOfflineFeature pending requests count: ${store.requestQueue.requests.length}`,
                                    ),
                                ),
                            ),
                    ]).pipe(
                        filter(([isInitialized]) => isInitialized),
                        switchMap(([, state]) => {
                            this.log.debug(
                                `HydrationEffects: serialize$: calling setState with ${state.requestQueue.requests.length} pending requests`,
                            );
                            return this.offlineStrategy.setState(state);
                        }),

                        tap((setStateSuccessful) => {
                            if (setStateSuccessful) {
                                this.log.info(
                                    `HydrationEffects: serialize$: state serialization and rehydration successful`,
                                );
                            } else {
                                this.log.error(
                                    `HydrationEffects: serialize$: error during state serialization and rehydration`,
                                );
                            }
                            this.offlineService.setStateHydration(setStateSuccessful);
                        }),
                        retryWhen(genericRetryStrategy(this.maxRetryAttempts)),
                    ),
                ),
            ),
        { dispatch: false },
    );

    public clearCache = createEffect(
        () =>
            this.action$.pipe(
                ofType(deleteCache),
                switchMap(() => this.offlineStrategy.deleteCache()),
                tap((setStateSuccessful) => {
                    if (setStateSuccessful) {
                        this.log.info(`HydrationEffects: resetCache: cache clear successful`);
                    }
                }),
            ),
        { dispatch: false },
    );
}
